import { Fragment, useState, useEffect, useMemo, useRef } from 'react'
import { useMutation } from '@apollo/client'
import { formatDistanceToNow } from 'date-fns'
import { Trash2, ExternalLink } from 'lucide-react'
import Swal from 'sweetalert2'
import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip'
import { Button } from '@/components/ui/button'
import { showNotification } from '@/utils/notifications'
import { logos } from '@/utils/platform-logos'
import { DELETE_FEED } from '@/graphql/publisher-dashboard-queries'
import Comments from './Comments'
import Sentiment from './Sentiment'
import _ from 'lodash'

const SAFE_URL_RE = /^https?:\/\//i

const FeedCard = ({ data, platform, index, onHeightChange, onDelete, onDeleting }) => {
    const childRef = useRef()
    const [profileImage, setProfileImage] = useState(data.profile.imageUrl)
    const media = useMemo(() => JSON.parse(data.media), [data.media])
    const keywords = useMemo(() => _.map(data.keywords, (keyword) => keyword.value), [data.keywords])

    const [deleteFeed, mutation] = useMutation(DELETE_FEED, {
        onError({ graphQLErrors }) {
            showNotification('Error: ' + graphQLErrors[0]?.message, 'error')
        },
        onCompleted: () => {
            onDelete(data.id)
        },
    })

    const handleDelete = () => {
        Swal.fire({
            title: 'Are you sure?',
            text: 'You are going to delete this feed.',
            icon: 'warning',
            showCancelButton: true,
            backdrop: 'rgba(60,60,60,0.7)',
            confirmButtonText: 'Delete',
        }).then((result) => {
            if (result.isConfirmed) {
                deleteFeed({ variables: { feedId: data.id } })
            }
        })
    }

    const openLink = (url) => {
        if (url && SAFE_URL_RE.test(url)) window.open(url, '_blank', 'noreferrer')
    }

    useEffect(() => {
        const childHeight = childRef.current?.clientHeight
        if (childHeight) onHeightChange(index, childHeight)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [childRef.current?.clientHeight])

    if (mutation.loading || onDeleting === data.id) {
        return (
            <div ref={childRef} className="tw:mb-0 tw:flex tw:items-center tw:justify-center tw:h-32">
                <div className="tw:animate-spin tw:w-6 tw:h-6 tw:border-2 tw:border-blue-400 tw:border-t-transparent tw:rounded-full" />
            </div>
        )
    }

    return (
        <Fragment>
            <div ref={childRef} className="tw:mb-0">
                <div className="card mb-0 w-100 border-0 box-shadow-none">
                    <div className="card-header pb-0 tw:flex tw:items-start tw:justify-between">
                        <div className="tw:flex tw:items-center tw:gap-3">
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button
                                        onClick={() => openLink(data.profile.url)}
                                        className="tw:w-10 tw:h-10 tw:rounded-full tw:bg-white tw:flex tw:items-center tw:justify-center tw:p-0 tw:border-0 tw:cursor-pointer tw:shrink-0"
                                    >
                                        <img
                                            src={profileImage || logos[platform.name]}
                                            alt={data.profile.username}
                                            onError={() => setProfileImage(logos[platform.name])}
                                            className="tw:w-6 tw:h-6 tw:object-contain tw:rounded-full"
                                        />
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent>Open Profile in {platform.name}</TooltipContent>
                            </Tooltip>
                            <div>
                                <h6 className="tw:mb-0 tw:text-sm tw:font-medium">{data.profile.username}</h6>
                                <p className="tw:mb-0 tw:text-xs tw:text-white">
                                    {formatDistanceToNow(new Date(data.postedAt), { addSuffix: true })}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={handleDelete}
                            className="tw:text-white tw:hover:text-red-400 tw:transition-colors tw:bg-transparent tw:border-0 tw:cursor-pointer"
                            aria-label="Delete feed"
                        >
                            <Trash2 size={16} />
                        </button>
                    </div>
                    <div className="card-body">
                        <div className="user-post">
                            {data.title && <h5 className="tw:mb-3">{data.title}</h5>}
                            {(media[0]?.type === 'image' || media[0]?.type === 'video') && (
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        {media[0]?.type === 'image' || media[0]?.previewImageUrl ? (
                                            <img
                                                src={media[0]?.type === 'image' ? media[0].url : media[0].previewImageUrl}
                                                alt="post"
                                                className="tw:w-full tw:rounded tw:cursor-pointer"
                                                onClick={() => openLink(data.url)}
                                            />
                                        ) : (
                                            <video controls className="tw:w-full">
                                                <source src={media[0].url} type="video/mp4" />
                                            </video>
                                        )}
                                    </TooltipTrigger>
                                    <TooltipContent>Open Post in {platform.name}</TooltipContent>
                                </Tooltip>
                            )}
                        </div>
                        <div className="comment-area tw:pt-3 tw:mb-5">
                            <p className="tw:text-sm tw:text-white tw:mb-0">{data.text}</p>
                            {data.comments?.length > 0 && (
                                <Comments comments={data.comments} platform={platform} />
                            )}
                            <div className="tw:py-3 tw:flex tw:flex-wrap tw:gap-1 tw:items-center">
                                <Sentiment feed={data} />
                                {keywords.map((keyword, i) => (
                                    <Badge key={i} variant="secondary" className="tw:rounded-full">
                                        {keyword}
                                    </Badge>
                                ))}
                            </div>
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => openLink(data.url)}
                                className="tw:gap-1"
                            >
                                <ExternalLink size={14} />
                                Read Post
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    )
}

export default FeedCard
